import { useEffect, useRef } from 'react';
import { Typography } from 'antd';
import { GlobalOutlined, CheckOutlined, StarFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { telegram } from '../../utils/telegram';
import OnboardingFrame, { OnboardingCard, CardSectionHeader } from './OnboardingFrame';

const { Text } = Typography;

const LANGUAGES: { value: string; flag: string }[] = [
  { value: 'uz', flag: '🇺🇿' },
  { value: 'ru', flag: '🇷🇺' },
  { value: 'en', flag: '🇬🇧' },
];

interface Props {
  supportedLanguages: string[];
  defaultLanguage: string;
  onChange: (supported: string[], defaultLang: string) => void;
  onNext: () => void;
  onBack: () => void;
  isLoading?: boolean;
}

export default function Step3Languages({
  supportedLanguages,
  defaultLanguage,
  onChange,
  onNext,
  isLoading,
}: Props) {
  const { t } = useTranslation('owner');

  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  useEffect(() => {
    const handleNext = () => onNextRef.current();
    telegram.setMainButton(
      isLoading
        ? t('onboarding.mainButton.loading')
        : t('onboarding.mainButton.submit'),
      handleNext,
    );
    return () => telegram.hideMainButton();
  }, [isLoading, t]);

  const toggleSupported = (lang: string) => {
    telegram.hapticFeedback.selection();
    const isSelected = supportedLanguages.includes(lang);
    let next: string[];
    if (isSelected) {
      next = supportedLanguages.filter((l) => l !== lang);
      if (next.length === 0) return;
    } else {
      next = [...supportedLanguages, lang];
    }
    const newDefault = next.includes(defaultLanguage) ? defaultLanguage : next[0];
    onChange(next, newDefault);
  };

  const setDefault = (lang: string) => {
    if (!supportedLanguages.includes(lang)) return;
    telegram.hapticFeedback.selection();
    onChange(supportedLanguages, lang);
  };

  return (
    <OnboardingFrame
      currentStep={3}
      badge={t('onboarding.step3.badge')}
      title={t('onboarding.step3.title')}
      subtitle={t('onboarding.step3.subtitle')}
    >
      {/* ─── Supported languages ───────────────────────────────────── */}
      <OnboardingCard>
        <CardSectionHeader
          icon={<GlobalOutlined />}
          iconColor="#1677ff"
          title={t('onboarding.step3.supportedLabel')}
          subtitle={t('onboarding.step3.supportedHelper')}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {LANGUAGES.map((lang) => {
            const isSelected = supportedLanguages.includes(lang.value);
            const isDefault = defaultLanguage === lang.value;
            return (
              <button
                key={lang.value}
                type="button"
                onClick={() => toggleSupported(lang.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: isSelected
                    ? '1.5px solid #1677ff'
                    : '1.5px solid var(--bl-divider, #ebebeb)',
                  background: isSelected
                    ? 'var(--bl-info-soft, #e7f1fe)'
                    : 'var(--bl-surface, #fff)',
                  cursor: 'pointer',
                  transition: 'border 0.2s ease, background 0.2s ease',
                  WebkitTapHighlightColor: 'transparent',
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 22, lineHeight: 1 }}>{lang.flag}</span>
                <Text
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--bl-text-strong, #0f172a)',
                  }}
                >
                  {t(`onboarding.languages.${lang.value}`)}
                </Text>
                {isDefault && isSelected && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      background: 'linear-gradient(135deg, #fa8c16, #faad14)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '3px 8px',
                      borderRadius: 999,
                      letterSpacing: 0.3,
                    }}
                  >
                    <StarFilled style={{ fontSize: 9 }} />
                    {t('onboarding.step3.defaultLabel')}
                  </span>
                )}
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: isSelected ? 'none' : '1.5px solid var(--bl-divider, #d9d9d9)',
                    background: isSelected
                      ? 'linear-gradient(135deg, #1677ff, #3390ec)'
                      : 'transparent',
                    color: '#fff',
                    fontSize: 11,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {isSelected && <CheckOutlined />}
                </div>
              </button>
            );
          })}
        </div>
      </OnboardingCard>

      {/* ─── Default language (when 2+ supported) ──────────────────── */}
      {supportedLanguages.length > 1 && (
        <OnboardingCard>
          <CardSectionHeader
            icon={<StarFilled />}
            iconColor="#fa8c16"
            title={t('onboarding.step3.defaultLabel')}
            subtitle={t('onboarding.step3.defaultHelper')}
          />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {LANGUAGES.filter((l) => supportedLanguages.includes(l.value)).map(
              (lang) => {
                const isActive = defaultLanguage === lang.value;
                return (
                  <button
                    key={lang.value}
                    type="button"
                    onClick={() => setDefault(lang.value)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 14px',
                      borderRadius: 999,
                      border: isActive
                        ? 'none'
                        : '1px solid var(--bl-divider, #ebebeb)',
                      background: isActive
                        ? 'linear-gradient(135deg, #fa8c16, #d46b08)'
                        : 'var(--bl-surface, #fff)',
                      color: isActive ? '#fff' : 'var(--bl-text-strong, #0f172a)',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      WebkitTapHighlightColor: 'transparent',
                      boxShadow: isActive
                        ? '0 4px 12px rgba(250,140,22,0.30)'
                        : 'none',
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{lang.flag}</span>
                    {t(`onboarding.languages.${lang.value}`)}
                  </button>
                );
              },
            )}
          </div>
        </OnboardingCard>
      )}
    </OnboardingFrame>
  );
}
